const getRefs = () => {
    const root = document.querySelector('#socsiem-calculator');

    if (!root) {
        return undefined;
    }

    return {
        fields: {
            organisation: {
                field: root.querySelector('#socsiem-calculator-organisation'),
                weights: {
                    '0-300': 1,
                    '300-800': 2,
                    '800-1500': 3
                }
            },
            interactions: {
                field: root.querySelector('#socsiem-calculator-interactions'),
                weights: {
                    Simple: 1,
                    Moderate: 2,
                    Complex: 3
                }
            },
            governance: {
                field: root.querySelector('#socsiem-calculator-governance'),
                weights: {
                    Light: 1,
                    Moderate: 2,
                    Heavy: 3
                }
            },
            securityControls: {
                field: root.querySelector('#socsiem-calculator-security-controls'),
                weights: {
                    Strong: 1,
                    Standard: 2,
                    Baseline: 3
                }
            }
        },
        summary: {
            managedServiceCosts: root.querySelector('#socsiem-calculator-summary-managed-service-costs'),
            siemAzureCosts: root.querySelector('#socsiem-calculator-summary-siem-azure-costs'),
            serviceImplementation: root.querySelector('#socsiem-calculator-summary-service-implmentation'),
            total: root.querySelector('#socsiem-calculator-summary-total')
        },
        calculateButton: root.querySelector('#socsiem-calculator-calculate')
    };
};

/**
 * Compute the final cost estimate components.
 * @param {number} organisationSizeWeight An organisation size weight [1, 2, 3].
 * @param {number} interactionsWeight [1, 2, 3].
 * @param {number} governanceWeight [1, 2, 3].
 * @param {number} securityControlsWeight [1, 2, 3].
 * @returns The final cost estimate components, or undefined if this is not possible.
 */
export const calculateCosts = (
    organisationSizeWeight,
    interactionsWeight,
    governanceWeight,
    securityControlsWeight) => {
    const score =
        organisationSizeWeight +
        interactionsWeight +
        governanceWeight +
        securityControlsWeight;

    const scoreValueMap = {
        4: 80000,
        5: 100000,
        6: 120000,
        7: 140000,
        8: 160000,
        9: 180000,
        10: 200000,
        11: 220000,
        12: 240000
    };

    const azureCostsForOrganisationSize = {
        1: 6000,
        2: 10000,
        3: 15000
    };

    const managedServiceCost = scoreValueMap[score];
    const siemAzureCost = azureCostsForOrganisationSize[organisationSizeWeight];
    const serviceImplementation = 20000;
    const total = managedServiceCost + siemAzureCost + serviceImplementation;

    if (
        isNaN(managedServiceCost) ||
        isNaN(siemAzureCost) ||
        isNaN(serviceImplementation) ||
        isNaN(total)) {
        return undefined;
    }

    return {
        managedServiceCost,
        siemAzureCost,
        serviceImplementation,
        total
    };
};

const init = () => {
    const refs = getRefs();

    if (!refs) {
        return;
    }

    const formatter = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: 'NZD',
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
    });

    const update = () => {
        // Get the value from each field
        const values = {
            organisation: refs.fields.organisation.weights[refs.fields.organisation.field.value],
            interactions: refs.fields.interactions.weights[refs.fields.interactions.field.value],
            governance: refs.fields.governance.weights[refs.fields.governance.field.value],
            securityControls: refs.fields.securityControls.weights[refs.fields.securityControls.field.value]
        };

        const costs = calculateCosts(
            values.organisation,
            values.interactions,
            values.governance,
            values.securityControls
        );

        // Summary and totals
        const asMonetary = (costKey) => {
            if (!costs) {
                return '';
            }

            const value = costs[costKey];

            return !costs || isNaN(value)
                ? ''
                : formatter.format(value);
        };

        refs.summary.managedServiceCosts.innerText = asMonetary('managedServiceCost');
        refs.summary.siemAzureCosts.innerText = asMonetary('siemAzureCost');
        refs.summary.serviceImplementation.innerText = asMonetary('serviceImplementation');
        refs.summary.total.innerText = asMonetary('total');
    };

    // Clear the cost fields
    const invalidate = () => {
        refs.summary.managedServiceCosts.innerText = '';
        refs.summary.siemAzureCosts.innerText = '';
        refs.summary.serviceImplementation.innerText = '';
        refs.summary.total.innerText = '';
    };

    refs.calculateButton.addEventListener('click', () => {
        update();
    });

    for (const { field } of Object.values(refs.fields)) {
        field.addEventListener('change', () => {
            invalidate();
        });
    }
};

if (typeof jQuery !== 'undefined') {
    jQuery(() => init());
}



// WEBPACK FOOTER //
// ./src/js/app/socsiem-calculator.js